<!-- app.vue -->
<template>
  <div id="app" class="app-wrapper safe-area">
    <!-- Contenu principal -->
    <div>
      <CookieConsentBanner />
      <!--      <NavigationBar-->
      <!--          :user="user"-->
      <!--          :is-logged-in="isLoggedIn"-->
      <!--      />-->
      <Toast/>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </div>
  </div>
</template>

<script setup>
import { useToast } from "primevue/usetoast"
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { permissionManager } from "~/services/PermissionManager.js"
import { useLocaleStore } from '~/stores/locale'
import { useAuthStore } from '~/stores/auth'

// ============================================================================
// HEAD CONFIGURATION
// ============================================================================
useHead({
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { key: 'theme-color', name: 'theme-color', content: 'light' }
  ],
  link: [
    { rel: 'icon', href: '/favicon.ico' }
  ],
  htmlAttrs: {
    lang: 'fr'
  }
})

// ============================================================================
// REACTIVE STATE
// ============================================================================
const toast = useToast()
const route = useRoute()
const { locale } = useI18n()
const { isShowing, progress, message, loadingSequence } = useSplashScreen()
const showWebSplash = ref(!Capacitor.isNativePlatform())
const isInitialized = ref(false)

// ============================================================================
// STORES - Initialisation différée
// ============================================================================
let localeStore = null
let auth = null
let watchersInitialized = false

// ============================================================================
// WAIT FOR PERSISTENCE HELPER
// ============================================================================
const waitForPersistence = () => {
  return new Promise((resolve) => {
    let retries = 0
    const maxRetries = 50 // 5 secondes max

    const checkPersistence = () => {
      // Vérifier si localStorage contient nos clés eco-
      const hasEcoKeys = Object.keys(localStorage).some(key => key.startsWith('eco-'))

      if (hasEcoKeys || retries >= maxRetries) {
        console.log(hasEcoKeys ? '✅ Persistance détectée' : '⚠️ Timeout persistance')
        resolve()
        return
      }

      retries++
      setTimeout(checkPersistence, 100)
    }

    // Démarrer la vérification après un délai initial
    setTimeout(checkPersistence, 200)
  })
}

// ============================================================================
// STORES INITIALIZATION
// ============================================================================
const initializeStores = async () => {
  if (isInitialized.value) return

  try {
    // 1. Attendre que la persistance soit prête
    await waitForPersistence()

    // 2. Initialiser les stores
    localeStore = useLocaleStore()
    auth = useAuthStore()

    console.log('🎯 Stores initialisés:', {
      auth: auth.$state,
      locale: localeStore.$state
    })

    // 3. Synchroniser la langue initialement
    locale.value = localeStore.language

    // 4. Configurer les watchers une seule fois
    if (!watchersInitialized) {
      setupWatchers()
      watchersInitialized = true
    }

    // 5. Initialiser l'authentification
    await initializeAuth()

    isInitialized.value = true
    console.log('✅ Application initialisée avec succès')

  } catch (error) {
    console.error('❌ Erreur lors de l\'initialisation:', error)
    isInitialized.value = true
  }
}

// ============================================================================
// WATCHERS SETUP
// ============================================================================
const setupWatchers = () => {
  // Watcher langue: Pinia -> Vue-i18n
  watch(
      () => localeStore.language,
      (newLang) => {
        if (locale.value !== newLang) {
          console.log('🌐 Langue changée vers:', newLang)
          locale.value = newLang
        }
      }
  )

  // Watcher langue: Vue-i18n -> Pinia
  watch(
      () => locale.value,
      (newLang) => {
        if (localeStore.language !== newLang) {
          localeStore.setLanguage(newLang)
        }
      }
  )

  // Watcher utilisateur
  watch(
      () => auth.user,
      (newUser, oldUser) => {
        if (newUser && !oldUser) {
          console.log('👤 Utilisateur connecté:', auth.userName)
        } else if (!newUser && oldUser) {
          console.log('🔒 Utilisateur déconnecté')
          window.location.reload()
        }
      }
  )


// ============================================================================
// WATCHERS
// ============================================================================
// Splash screen
  watch(isShowing, (newValue) => {
    if (!Capacitor.isNativePlatform()) {
      showWebSplash.value = newValue
    }
  })

// Route changes
  watch(() => route.fullPath, (newPath) => {
    if (newPath.includes('#')) {
      const fullUrl = window.location.origin + newPath
      handleHashUrl(fullUrl)
    }
  })

  console.log('👁️ Watchers configurés')
}

// ============================================================================
// AUTHENTICATION
// ============================================================================
const initializeAuth = async () => {
  try {
    // Si déjà authentifié depuis la persistance
    if (auth.isAuthenticated && auth.user) {
      console.log('🔐 Session restaurée:', auth.userName)
      auth.startRefreshTimer()
      return
    }

    // Vérifier avec le serveur
    console.log('🔍 Vérification auth serveur...')
    await auth.checkAuth()

  } catch (error) {
    console.log('⚠️ Auth check échoué:', error.message)

    // Si on est sur une route protégée, rediriger
    const protectedRoutes = ['/dashboard', '/admin', '/profile']
    const isProtectedRoute = protectedRoutes.some(route =>
        window.location.pathname.startsWith(route)
    )

    if (isProtectedRoute) {
      await auth.logout()
      await navigateTo('/auth/login')
    }
  }
}

// ============================================================================
// HASH URL HANDLING
// ============================================================================
const scrollToSection = (sectionId) => {
  nextTick(() => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  })
}

const handleHashUrl = (url) => {
  try {
    const urlObj = new URL(url, window.location.origin)
    const hash = urlObj.hash

    if (hash && hash.startsWith('#')) {
      const sectionName = hash.substring(1)
      const pathWithoutHash = urlObj.pathname

      navigateTo(pathWithoutHash, { replace: true })
      setTimeout(() => scrollToSection(sectionName), 100)
      return true
    }
  } catch (error) {
    console.warn('Erreur handleHashUrl:', error)
  }

  return false
}

// ============================================================================
// PLATFORM INITIALIZATION
// ============================================================================
const initializePlatform = async () => {
  try {
    if (!Capacitor.isNativePlatform()) {
      // PWA: Splash screen
      await loadingSequence()
      showWebSplash.value = false
    } else {
      // Native: Permissions
      const platform = Capacitor.getPlatform()

      if (platform === 'android') {
        await permissionManager.diagnosePermissions()
        await permissionManager.requestAllPermissions({
          timeout: 45000,
          retryCount: 1,
          showExplanation: true
        })
      }

      // StatusBar
      await StatusBar.setOverlaysWebView({ overlay: false })
      await StatusBar.setStyle({ style: Style.Light })

      showWebSplash.value = false
    }
  } catch (error) {
    console.error('❌ Erreur initialisation plateforme:', error)
    showWebSplash.value = false
  }
}

// ============================================================================
// LIFECYCLE
// ============================================================================
onMounted(async () => {
  console.log('🚀 App mounted')
  await nextTick()
  // 1. Hash URLs
  const currentUrl = window.location.href
  handleHashUrl(currentUrl)

  // 2. Initialisation séquentielle
  await Promise.all([
    initializePlatform(),
    initializeStores()
  ])

  console.log('🎉 App prête!')
})


// DEBUG: localStorage changes (development only)
if (process.env.NODE_ENV === 'development') {
  onMounted(() => {
    setTimeout(() => {
      console.group('🔍 DEBUG STORES')
      console.log('LocalStorage eco- keys:',
          Object.keys(localStorage).filter(k => k.startsWith('eco-'))
      )
      console.log('Auth state:', auth?.$state)
      console.log('Locale state:', localeStore?.$state)
      console.groupEnd()
    }, 3000)
  })
}

nextTick(async ()=>{
  // Hash changes
  window.addEventListener('hashchange', (event) => {
    handleHashUrl(event.newURL)
  })

  // Click interception pour les liens hash
  window.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]')
    if (link) {
      const href = link.getAttribute('href')
      if (href && href.includes('#')) {
        event.preventDefault()
        handleHashUrl(link.href)
      }
    }
  })
})
</script>


<style scoped>
/* global.css ou dans un style global */
.safe-area {
  padding-top: env(safe-area-inset-top);
}

/* Responsive */
@media (max-width: 768px) {
  .splash-content {
    max-width: 250px;
  }

  .corn-icon {
    font-size: 3rem;
  }

  .splash-logo h1 {
    font-size: 1.5rem;
  }
}
/* width */
::-webkit-scrollbar {
  width: 8px;
}

/* Track */
::-webkit-scrollbar-track {
  background: #d5e4d281;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: #a6c0a6;
  border-radius: 5px;
}
::-webkit-scrollbar:horizontal {
  height: 8px; /* Modifier la hauteur selon tes besoins */
}
/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>